import { useState } from "react";
import type { ReactElement } from "react";
import type { TicketKalender, TicketKarte, TicketSpalte, TicketZeile } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen } from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import type { AblageZiel } from "./TicketZeitleiste.js";
import { monatBeschriftung, popupFuerTag } from "./ticketFormat.js";

/** „VERSCHIEBEN …" (F22): der Weg ohne Maus und die Rückfallebene am Telefon — Spalte und Ziel
 *  (Jetzt, Datum, Monat, Datum behalten). Das endgültige Datum rechnet der Server (F18); ein
 *  Wochenende oder Feiertag als Datum ist erlaubt, der Dialog warnt nur (119). Strikter Dialog:
 *  Kreuz, Escape und Abbrechen fragen bei geänderter Eingabe nach. */

type Art = "JETZT" | "TAG" | "MONAT" | "KOPF";

export function TicketVerschieben(p: {
  t: TicketKarte; spalten: readonly TicketSpalte[]; zeilen: readonly TicketZeile[]; heute: string;
  kalender?: readonly TicketKalender[];
  onAblage: (z: AblageZiel) => void; onAbbrechen: () => void;
}): ReactElement {
  const monate = p.zeilen.filter((z) => z.art === "MONAT" || z.art === "SPAETER");
  // Vorgabe „Datum behalten“ (Gesamtreview I1): Wer nur die Spalte wechselt, wirkt wie das Ziehen
  // auf den Spaltenkopf — ein überfälliges Ticket schickt kein Datum in der Vergangenheit (422),
  // ein undatiertes bekommt keins. Wer „Datum“ wählt, startet frühestens bei heute.
  const startDatum = p.t.resubmitDate !== null && p.t.resubmitDate > p.heute ? p.t.resubmitDate : p.heute;
  const [spalte, setSpalte] = useState(p.t.columnId);
  const [art, setArt] = useState<Art>("KOPF");
  const [datum, setDatum] = useState(startDatum);
  const [monat, setMonat] = useState(monate[0]?.von ?? "");
  const ziel = p.spalten.find((s) => s.id === spalte);
  const ohneLeiste = ziel?.timelineMode === "NONE";
  const warnung = !ohneLeiste && art === "TAG" && datum !== "" ? popupFuerTag(datum, p.kalender ?? []) : "";
  const geaendert = spalte !== p.t.columnId || art !== "KOPF" || datum !== startDatum;

  function absenden(): void {
    const name = ziel?.name ?? "";
    if (ohneLeiste || art === "KOPF") { p.onAblage({ columnId: spalte, ziel: "KOPF", datum: null, text: name }); return; }
    if (art === "JETZT") { p.onAblage({ columnId: spalte, ziel: "JETZT", datum: null, text: `${name} · Jetzt` }); return; }
    if (art === "TAG") { p.onAblage({ columnId: spalte, ziel: "TAG", datum, text: name }); return; }
    const spaeter = monate.find((z) => z.von === monat)?.art === "SPAETER";
    p.onAblage({ columnId: spalte, ziel: spaeter ? "SPAETER" : "MONAT", datum: monat, text: name });
  }
  async function abbrechen(): Promise<void> {
    if (await darfAbbrechen(geaendert)) p.onAbbrechen();
  }
  const gesperrt = !ohneLeiste && ((art === "TAG" && datum === "") || (art === "MONAT" && monat === ""));

  return (
    <Modal titel={`${p.t.ticketNumber} verschieben`} onAbbrechen={() => { void abbrechen(); }} breite="standard">
      {/* Beschriftung oben, eine Spalte: Die Segmentgruppe „Ziel“ steht in einer Zeile (Sichtprobe 10). */}
      <Formular beschriftung="oben">
        <Auswahl beschriftung="Spalte" wert={spalte} onGeaendert={(d) => setSpalte(d.wert)}>
          {p.spalten.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Auswahl>
        {!ohneLeiste && (
          <Optionen beschriftung="Ziel" darstellung="segmente" richtung="waagerecht" wert={art}
            onGeaendert={(d) => { if (d.wert !== undefined) setArt(d.wert as Art); }}>
            <option value="JETZT">Jetzt</option>
            <option value="TAG">Datum</option>
            <option value="MONAT">Monat</option>
            <option value="KOPF">Datum behalten</option>
          </Optionen>
        )}
        {!ohneLeiste && art === "TAG" && (
          <Feld id="verschieben-datum" label="Datum" typ="date" wert={datum} onChange={setDatum} bearbeitbar />
        )}
        {!ohneLeiste && art === "MONAT" && (
          <Auswahl beschriftung="Monat" wert={monat} onGeaendert={(d) => setMonat(d.wert)}>
            {monate.map((z) => (
              <option key={z.index} value={z.von}>{z.art === "SPAETER" ? "später" : monatBeschriftung(z.von!)}</option>
            ))}
          </Auswahl>
        )}
      </Formular>
      {warnung !== "" && <Hinweis art="warnung">{`${warnung} — das Datum bleibt so.`}</Hinweis>}
      <ModalFuss speichernText="Verschieben" speichernGesperrt={gesperrt} onSpeichern={absenden}
        onAbbrechen={() => { void abbrechen(); }} />
    </Modal>
  );
}
